import React from "react";
import {Card} from "../../components/core/Card.jsx";
import {Badge} from "../../components/core/Badge.jsx";
import {Icon} from "../../components/core/Icon.jsx";
import {Button} from "../../components/core/Button.jsx";
import {IconButton} from "../../components/core/IconButton.jsx";
import {Tooltip} from "../../components/feedback/Tooltip.jsx";
import {H2,Eyebrow,Glitch} from "./Shell.jsx";

const DIAGRAM_STEPS=[
  {key:"entra",label:"Entra el trabajo",
   copy:"Llega un correo de la clínica con un STL adjunto. El Agente vigila la bandeja: no hace falta que yo lo abra.",
   inputs:[0,1,2],stage:-1,tools:[],outputs:[]},
  {key:"percibe",label:"Percibe",
   copy:"Lee el mensaje y el archivo, y saca los datos: qué clínica es, qué trabajo pide, qué material y para cuándo.",
   inputs:[0],stage:0,tools:[],outputs:[]},
  {key:"razona",label:"Razona",
   copy:"Comprueba contra mis reglas: si falta el antagonista para, si el plazo no entra me avisa, y si todo está lo pasa a producción.",
   inputs:[],stage:1,tools:[],outputs:[]},
  {key:"actua",label:"Usa herramientas",
   copy:"Aquí está la diferencia con un chatbot: el Agente hace cosas. Abre el laminador, escribe en la carpeta LAMINAR, manda el correo y rellena el albarán.",
   inputs:[],stage:2,tools:[0,1,2,3],outputs:[]},
  {key:"entrega",label:"Entrega",
   copy:"El trabajo sale por los tres lados a la vez: la impresora tiene el archivo, la clínica tiene su aviso y el caso queda con su albarán.",
   inputs:[],stage:2,tools:[0,1,2,3],outputs:[0,1,2]},
  {key:"aprende",label:"Deja rastro",
   copy:"Todo queda escrito en el caso. Si algo sale torcido lo veo en el registro y ajusto la regla, así que la próxima vez lo hace mejor.",
   inputs:[],stage:3,tools:[],outputs:[0,1,2]}
];

const D_INPUTS=[["mail","Correo de clínica","STL adjunto"],["message-circle","WhatsApp","foto o archivo"],["scan-line","Escáner intraoral","carpeta compartida"]];
const D_STAGES=[
  ["eye","Percibe","lee el mensaje y el archivo"],
  ["brain","Razona","aplica mis reglas del laboratorio"],
  ["wrench","Usa herramientas","hace el trabajo de verdad"],
  ["database","Deja rastro","escribe el caso y el registro"]
];
const D_TOOLS=[["layers","Laminador"],["folder","Carpeta LAMINAR"],["send","Correo saliente"],["file-text","Albarán"]];
const D_OUTPUTS=[["printer","Photon Mono M7","el archivo, en cola"],["bell","Aviso a la clínica","con el plazo real"],["check-check","Caso cerrado","albarán archivado"]];

export function AgentDiagram(){
  const [i,setI]=React.useState(0);
  const [playing,setPlaying]=React.useState(true);
  React.useEffect(()=>{
    if(!playing)return;
    const t=setTimeout(()=>setI(v=>(v+1)%DIAGRAM_STEPS.length),3400);
    return ()=>clearTimeout(t);
  },[i,playing]);
  const s=DIAGRAM_STEPS[i];
  const on=(arr,n)=>arr.includes(n);

  return (
    <div>
      <style dangerouslySetInnerHTML={{__html:`
@keyframes dpFlow{0%{transform:translateX(-6px);opacity:0}18%{opacity:1}82%{opacity:1}100%{transform:translateX(var(--dpRun,54px));opacity:0}}
@keyframes dpPulse{0%,100%{box-shadow:0 0 0 0 rgba(0,191,199,.42)}50%{box-shadow:0 0 0 7px rgba(0,191,199,0)}}
@keyframes dpFade{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}
.dpNode{transition:border-color var(--dur-base) var(--ease-standard),background-color var(--dur-base) var(--ease-standard),color var(--dur-base) var(--ease-standard),opacity var(--dur-base) var(--ease-standard)}
.dpWire{position:relative;flex:1;height:1px;background:var(--line-default);transition:background-color var(--dur-base) var(--ease-standard)}
.dpWire.on{background:var(--cyan-500)}
.dpWire.on::after{content:"";position:absolute;top:-2px;left:0;width:5px;height:5px;border-radius:50%;background:var(--cyan-500);animation:dpFlow 1.15s var(--ease-standard) infinite}
@media (prefers-reduced-motion:reduce){.dpWire.on::after{animation:none}}
      `}}/>

      <div style={{display:"flex",flexWrap:"wrap",alignItems:"flex-end",justifyContent:"space-between",gap:"24px 32px"}}>
        <div>
          <Eyebrow>Cómo funciona</Eyebrow>
          <h2 style={{fontSize:H2,marginTop:16,maxWidth:"24ch"}}>Qué es exactamente un <Glitch>Agente de IA</Glitch></h2>
        </div>
        <p style={{fontSize:16,lineHeight:1.62,color:"var(--fg-2)",maxWidth:"44ch"}}>
          Un chatbot te contesta. Un Agente coge el trabajo, decide qué hacer con él y lo hace: abre programas, mueve archivos y escribe correos. Mira un caso real pasando por dentro.
        </p>
      </div>

      <Card pad="none" style={{marginTop:40,overflow:"hidden"}}>
        <div style={{display:"flex",alignItems:"center",gap:12,padding:"14px 20px",borderBottom:"1px solid var(--line-hairline)",background:"var(--surface-sunken)"}}>
          <span style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--fg-3)"}}>caso 2026-0418 · Clínica Vega</span>
          <div style={{display:"flex",gap:6,marginLeft:"auto"}}>
            {DIAGRAM_STEPS.map((st,n)=>(
              <button key={st.key} onClick={()=>{setI(n);setPlaying(false)}} aria-label={st.label}
                style={{all:"unset",cursor:"pointer",height:4,width:n===i?26:14,borderRadius:2,
                  background:n===i?"var(--cyan-500)":"var(--neutral-200)",transition:"all var(--dur-base) var(--ease-standard)"}}/>
            ))}
          </div>
          <Tooltip content={playing?"Pausar":"Reproducir"} side="left">
            <IconButton icon={playing?"pause":"play"} label={playing?"Pausar":"Reproducir"} size="sm" variant="outline" onClick={()=>setPlaying(p=>!p)}/>
          </Tooltip>
        </div>

        <div className="dp-diagram" style={{padding:"clamp(20px,3vw,28px) clamp(14px,2.5vw,20px)"}}>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <div style={{fontFamily:"var(--font-display)",fontSize:10,fontWeight:600,letterSpacing:".1em",textTransform:"uppercase",color:"var(--fg-3)"}}>Entra</div>
            {D_INPUTS.map(([ic,t,m],n)=>{const a=on(s.inputs,n);return (
              <div key={t} className="dpNode" style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:"var(--radius-2)",
                background:a?"var(--surface-accent-soft)":"var(--surface-card)",border:"1px solid "+(a?"var(--cyan-500)":"var(--line-default)"),opacity:a?1:.55}}>
                <span style={{color:a?"var(--cyan-700)":"var(--fg-3)"}}><Icon name={ic} size={16} strokeAlign="middle"/></span>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:13.5,fontWeight:a?600:400,color:"var(--fg-1)",lineHeight:1.25}}>{t}</div>
                  <div style={{fontFamily:"var(--font-mono)",fontSize:10,color:"var(--fg-3)"}}>{m}</div>
                </div>
              </div>);})}
          </div>

          <div className="dp-wirecell" style={{display:"flex",alignItems:"center","--dpRun":"22px"}}>
            <div className={"dpWire"+(s.inputs.length?" on":"")}/>
          </div>

          <div style={{border:"1px solid "+(s.stage>=0?"var(--teal-700)":"var(--line-default)"),borderRadius:"var(--radius-3)",
            background:"var(--teal-900)",backgroundImage:"var(--pattern-grid-on-brand)",padding:16,transition:"border-color var(--dur-base) var(--ease-standard)"}}>
            <div style={{display:"flex",alignItems:"center",gap:8,paddingBottom:12,borderBottom:"1px solid var(--line-on-brand)"}}>
              <span style={{color:"var(--cyan-400)"}}><Icon name="bot" size={16} strokeAlign="middle"/></span>
              <span style={{fontFamily:"var(--font-display)",fontSize:13,fontWeight:600,color:"#fff",flex:1}}>El Agente</span>
              <span style={{fontFamily:"var(--font-mono)",fontSize:10,color:"var(--cyan-300)"}}>{s.stage>=0?"trabajando":"a la espera"}</span>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:12}}>
              {D_STAGES.map(([ic,t,m],n)=>{const a=s.stage===n;const done=s.stage>n;return (
                <div key={t} className="dpNode" style={{display:"flex",alignItems:"center",gap:10,padding:"9px 11px",borderRadius:"var(--radius-2)",
                  background:a?"var(--cyan-500)":"rgba(255,255,255,.05)",border:"1px solid "+(a?"var(--cyan-500)":"var(--line-on-brand)"),
                  animation:a?"dpPulse 1.6s var(--ease-standard) infinite":"none"}}>
                  <span style={{color:a?"var(--teal-900)":done?"var(--cyan-400)":"var(--neutral-400)"}}><Icon name={ic} size={15} strokeAlign="middle"/></span>
                  <div style={{flex:1}}>
                    <div style={{fontFamily:"var(--font-display)",fontSize:13,fontWeight:600,color:a?"var(--teal-900)":"#fff"}}>{t}</div>
                    <div style={{fontSize:11,color:a?"var(--teal-800)":"var(--neutral-400)"}}>{m}</div>
                  </div>
                  {done&&<span style={{color:"var(--cyan-400)"}}><Icon name="check" size={13} strokeAlign="middle"/></span>}
                </div>);})}
            </div>
            <div style={{marginTop:14,paddingTop:12,borderTop:"1px solid var(--line-on-brand)"}}>
              <div style={{fontFamily:"var(--font-display)",fontSize:9.5,fontWeight:600,letterSpacing:".1em",textTransform:"uppercase",color:"var(--neutral-400)"}}>Sus herramientas</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:9}}>
                {D_TOOLS.map(([ic,t],n)=>{const a=on(s.tools,n);return (
                  <span key={t} className="dpNode" style={{display:"inline-flex",alignItems:"center",gap:6,padding:"5px 9px",borderRadius:"var(--radius-pill)",
                    background:a?"var(--cyan-500)":"transparent",border:"1px solid "+(a?"var(--cyan-500)":"var(--line-on-brand)"),
                    color:a?"var(--teal-900)":"var(--neutral-300)",fontSize:11.5,whiteSpace:"nowrap"}}>
                    <Icon name={ic} size={12} strokeAlign="middle"/>{t}
                  </span>);})}
              </div>
            </div>
          </div>

          <div className="dp-wirecell" style={{display:"flex",alignItems:"center","--dpRun":"22px"}}>
            <div className={"dpWire"+(s.outputs.length?" on":"")}/>
          </div>

          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <div style={{fontFamily:"var(--font-display)",fontSize:10,fontWeight:600,letterSpacing:".1em",textTransform:"uppercase",color:"var(--fg-3)"}}>Sale</div>
            {D_OUTPUTS.map(([ic,t,m],n)=>{const a=on(s.outputs,n);return (
              <div key={t} className="dpNode" style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:"var(--radius-2)",
                background:a?"var(--surface-accent-soft)":"var(--surface-card)",border:"1px solid "+(a?"var(--cyan-500)":"var(--line-default)"),opacity:a?1:.55}}>
                <span style={{color:a?"var(--cyan-700)":"var(--fg-3)"}}><Icon name={ic} size={16} strokeAlign="middle"/></span>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:13.5,fontWeight:a?600:400,color:"var(--fg-1)",lineHeight:1.25}}>{t}</div>
                  <div style={{fontFamily:"var(--font-mono)",fontSize:10,color:"var(--fg-3)"}}>{m}</div>
                </div>
              </div>);})}
          </div>
        </div>

        <div style={{display:"flex",gap:"8px 20px",flexWrap:"wrap",alignItems:"flex-start",padding:"18px 20px",borderTop:"1px solid var(--line-hairline)",background:"var(--surface-sunken)"}}>
          <span style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--cyan-700)",paddingTop:3,flex:"0 0 auto",whiteSpace:"nowrap"}}>0{i+1} / 0{DIAGRAM_STEPS.length}</span>
          <div key={s.key} style={{animation:"dpFade var(--dur-base) var(--ease-standard)"}}>
            <div style={{fontFamily:"var(--font-display)",fontSize:15,fontWeight:600}}>{s.label}</div>
            <p style={{fontSize:14.5,lineHeight:1.6,color:"var(--fg-2)",marginTop:4,maxWidth:"78ch"}}>{s.copy}</p>
          </div>
        </div>
      </Card>

      <div className="dp-g3" style={{gap:24,marginTop:32}}>
        {[["circle-slash","No es un chatbot","No espera a que le preguntes. Vigila la entrada y arranca solo."],
          ["wrench","Tiene manos","Puede abrir programas, mover archivos y mandar correos por ti."],
          ["shield-check","Trabaja con mis reglas","Si algo no está claro para y me pregunta. No inventa plazos."]].map(([ic,t,d])=>(
          <div key={t} style={{borderTop:"2px solid var(--teal-900)",paddingTop:14}}>
            <span style={{color:"var(--cyan-700)"}}><Icon name={ic} size={18} strokeAlign="middle"/></span>
            <h4 style={{marginTop:10}}>{t}</h4>
            <p style={{fontSize:14,lineHeight:1.6,color:"var(--fg-2)",marginTop:6}}>{d}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
